import React, { useState, useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import AttachedVideosFormDialog from '../detailDialogForms/AttachedVideosFormDialog';
import { useGetWarrantyClaimVideosQuery, useGetWarrantyClaimsByIdQuery } from '../../../../../../../api/endpoints/warrantyClaims';
import { useLanguage } from '../../../../../../../contexts/LanguageContext';
import { formatDateOnly } from '../../../../../../../utils/dateUtils';
import { usePermissions } from '../../../../../../../hooks/usePermissions';
import { WarrantyClaimStatus } from '../../../../../../../utils/warrantyClaimsEnums';

const DISABLED_STATUSES = [WarrantyClaimStatus.RETURNED_TO_KARSAN, WarrantyClaimStatus.ACCEPTED, WarrantyClaimStatus.REJECTED];

const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const formatDuration = (seconds) => {
    if (!seconds) return '-';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
};

const AttachedVideosTable = ({ claimId }) => {
    const { th, getCurrentLanguageId } = useLanguage();
    const { hasDealerRole, hasServiceDealerRole, hasSalesDealerRole } = usePermissions();

    const [dialogVisible, setDialogVisible] = useState(false);
    const [editingVideo, setEditingVideo] = useState(null);
    const [lazyParams, setLazyParams] = useState({ rows: 10, sortField: null, sortOrder: null });

    const { data: videosData, isLoading, refetch } = useGetWarrantyClaimVideosQuery(
        { claimId, includeInactive: false, generateDownloadUrls: true, urlExpirationMinutes: 60 },
        { skip: !claimId, refetchOnMountOrArgChange: true }
    );
    const { data: warrantyClaimGetById } = useGetWarrantyClaimsByIdQuery({ id: claimId, languageId: getCurrentLanguageId() });

    const disabledStatuses = DISABLED_STATUSES.includes(warrantyClaimGetById?.status);
    const disabledForRole =
        (hasDealerRole() || hasServiceDealerRole() || hasSalesDealerRole()) &&
        warrantyClaimGetById?.status === WarrantyClaimStatus.WAITING;

    const videos = useMemo(() => {
        if (Array.isArray(videosData?.data)) return videosData.data;
        if (Array.isArray(videosData)) return videosData;
        return [];
    }, [videosData]);

    const sortedData = useMemo(() => {
        if (!lazyParams.sortField || lazyParams.sortOrder === 0) return videos;

        return [...videos].sort((a, b) => {
            let aVal = a[lazyParams.sortField];
            let bVal = b[lazyParams.sortField];

            if (aVal == null && bVal == null) return 0;
            if (aVal == null) return lazyParams.sortOrder;
            if (bVal == null) return -lazyParams.sortOrder;

            if (typeof aVal === 'string') aVal = aVal.toLowerCase();
            if (typeof bVal === 'string') bVal = bVal.toLowerCase();

            if (typeof aVal === 'number' && typeof bVal === 'number') {
                return lazyParams.sortOrder * (aVal - bVal);
            }
            if (aVal < bVal) return -lazyParams.sortOrder;
            if (aVal > bVal) return lazyParams.sortOrder;
            return 0;
        });
    }, [videos, lazyParams.sortField, lazyParams.sortOrder]);

    const handleNewVideo = () => {
        setEditingVideo(null);
        setDialogVisible(true);
    };

    const handleEdit = (rowData) => {
        setEditingVideo(rowData);
        setDialogVisible(true);
    };

    const handleSuccess = () => {
        refetch();
        setDialogVisible(false);
    };

    const onSort = (event) => {
        setLazyParams((prev) => ({ ...prev, sortField: event.sortField, sortOrder: event.sortOrder }));
    };

    const fileNameTemplate = (rowData) => {
        const name = rowData.fileName?.split('/').pop() || rowData.fileName || '-';
        return (
            <div className="flex align-items-center gap-2">
                <i className="pi pi-video text-primary" style={{ fontSize: '1rem' }} />
                <span>{name}</span>
            </div>
        );
    };

    const downloadTemplate = (rowData) => {
        if (!rowData.downloadUrl) return null;
        return (
            <a href={rowData.downloadUrl} target="_blank" rel="noopener noreferrer" title="Videoyu İndir">
                <i className="pi pi-download cursor-pointer text-blue-500 hover:text-blue-700" style={{ fontSize: '1.1rem' }} />
            </a>
        );
    };

    const actionBodyTemplate = (rowData) => (
        <div className="flex gap-2 justify-content-center">
            <i
                onClick={() => handleEdit(rowData)}
                className="pi pi-eye cursor-pointer text-blue-500 hover:text-blue-700"
                style={{ fontSize: '1.1rem' }}
                title="Detay / Sil"
            />
        </div>
    );

    return (
        <div>
            <div className="flex justify-content-between align-items-center mb-3 mt-3">
                <Button
                    label="Video Ekle"
                    icon="pi pi-plus"
                    onClick={handleNewVideo}
                    disabled={disabledStatuses || disabledForRole}
                />
            </div>

            <DataTable
                value={sortedData}
                className="p-datatable-gridlines"
                showGridlines
                paginator
                rows={lazyParams.rows}
                onSort={onSort}
                sortField={lazyParams.sortField}
                sortOrder={lazyParams.sortOrder}
                dataKey="id"
                loading={isLoading}
                emptyMessage="Kayıt bulunamadı."
                rowsPerPageOptions={[5, 10, 25, 50]}
                tableStyle={{ minWidth: '60rem' }}
            >
                <Column
                    body={actionBodyTemplate}
                    style={{ width: '5%', minWidth: '33px' }}
                    bodyClassName="text-center"
                    headerClassName="text-center"
                    exportable={false}
                />
                <Column field="fileName" header="Dosya Adı" sortable body={fileNameTemplate} />
                <Column field="title" header="Başlık" sortable body={(row) => row.title || '-'} />
                <Column field="description" header="Açıklama" sortable body={(row) => row.description || '-'} />
                <Column field="fileSize" header="Boyut" sortable body={(row) => formatFileSize(row.fileSize)} style={{ minWidth: '100px' }} />
                <Column field="durationSeconds" header="Süre" sortable body={(row) => formatDuration(row.durationSeconds)} style={{ minWidth: '80px' }} />
                <Column
                    header="İndir"
                    body={downloadTemplate}
                    style={{ width: '5%', minWidth: '60px' }}
                    bodyClassName="text-center"
                    headerClassName="text-center"
                    exportable={false}
                />
                <Column
                    field="createdDate"
                    header={th('warrantyClaims.detail.documents.createdDate')}
                    sortable
                    body={(row) => formatDateOnly(row?.createdDate)}
                />
            </DataTable>

            <AttachedVideosFormDialog
                disabledStatuses={disabledStatuses || disabledForRole}
                claimId={claimId}
                dialogVisible={dialogVisible}
                hideDialog={() => setDialogVisible(false)}
                editingVideo={editingVideo}
                onSuccess={handleSuccess}
            />
        </div>
    );
};

export default AttachedVideosTable;
